import { Button, Image } from '../../../modules/design-system';
import type { IframeUnopenedOffer } from '../../../modules/api';

export interface UnopenedPackCardProps {
  offer: IframeUnopenedOffer;
  onOpen: (offer: IframeUnopenedOffer) => void;
}

/**
 * Vertical pack tile for the Repackz tab. Figma 302:24263.
 *
 * The tile background is flat grey by default and operator-themable via
 * Design Constructor (`--co-MyPacksUnopenedPackCard-*`):
 *   • `bg`        — solid background colour (color knob).
 *   • `bgImage`   — `background-image` value, e.g. `url("…")` or a CSS
 *                   gradient. Defaults to `none`; when set it overlays the
 *                   `bg` colour and is rendered with `cover` + center.
 *   • `borderColor` / `borderWidth` — frame.
 *   • `titleColor` / `subtitleColor` / `buttonFill` / `buttonText`.
 */
export function UnopenedPackCard({ offer, onOpen }: UnopenedPackCardProps) {
  const priceLabel = `${offer.unopenedCount} Unopened Pack`;
  const disabled = offer.firstUnopenedOrderId == null;

  return (
    <article
      data-co-component="MyPacksUnopenedPackCard"
      className="
        relative aspect-[15/14] overflow-hidden rounded-[20px]
        shadow-[0_64px_34px_0_rgba(0,0,0,0.05)]
        @[600px]:aspect-[485/311] @[600px]:rounded-[28px]
      "
      aria-label={offer.title}
      style={{
        // Each gradient slot reads its own `MyPacksUnopenedPackCard-*`
        // knob first; if unset, it falls through to the matching
        // `MyPacksItemCard-*` value so an un-customised Packs tab still
        // matches the Cards tab. `--co-MyPacksUnopenedPackCard-bgImage`
        // stacks on top of the gradient when the operator wants a hero
        // illustration per tile.
        backgroundImage:
          'var(--co-MyPacksUnopenedPackCard-bgImage, none), ' +
          'linear-gradient(' +
          'var(--co-MyPacksUnopenedPackCard-bgDirection, var(--co-MyPacksItemCard-bgDirection, to bottom)), ' +
          'color-mix(in srgb, var(--co-MyPacksUnopenedPackCard-bgFrom, var(--co-MyPacksItemCard-bgFrom, rgba(255,255,255,0.20))) ' +
          'calc(var(--co-MyPacksUnopenedPackCard-bgOpacity, var(--co-MyPacksItemCard-bgOpacity, 1)) * 100%), transparent) 0%, ' +
          'color-mix(in srgb, var(--co-MyPacksUnopenedPackCard-bgTo, var(--co-MyPacksItemCard-bgTo, rgba(153,153,153,0.20))) ' +
          'calc(var(--co-MyPacksUnopenedPackCard-bgOpacity, var(--co-MyPacksItemCard-bgOpacity, 1)) * 100%), transparent) 100%), ' +
          'linear-gradient(90deg, ' +
          'var(--co-MyPacksUnopenedPackCard-darkTint, var(--co-MyPacksItemCard-darkTint, rgba(0,0,0,0.18))) 0%, ' +
          'var(--co-MyPacksUnopenedPackCard-darkTint, var(--co-MyPacksItemCard-darkTint, rgba(0,0,0,0.18))) 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backdropFilter: 'blur(var(--co-MyPacksItemCard-glassBlur, 27px))',
        WebkitBackdropFilter: 'blur(var(--co-MyPacksItemCard-glassBlur, 27px))',
        borderStyle: 'solid',
        borderWidth: 'var(--co-MyPacksUnopenedPackCard-borderWidth, var(--co-MyPacksItemCard-borderWidth, 2px))',
        borderColor: 'var(--co-MyPacksUnopenedPackCard-borderColor, var(--co-MyPacksItemCard-borderColor, rgba(171,174,179,0.24)))',
      }}
    >
      {offer.packImageUrl && (
        <Image
          src={offer.packImageUrl}
          alt={`${offer.title} pack`}
          className="absolute left-1/2 top-[52%] aspect-[246/307] h-[100%] -translate-x-1/2"
          imgClassName="absolute inset-0 h-full w-full object-contain object-top"
        />
      )}

      <div className="relative flex flex-col items-center gap-[14px] pt-[18px]">
        <div className="flex flex-col items-center gap-[10px] text-center">
          <h3
            className="
              px-3 font-bold uppercase
              text-[18px] leading-[22px] tracking-[-0.3px]
              @[600px]:text-[24px] @[600px]:leading-[28px] @[600px]:tracking-[-0.48px]
            "
            style={{ color: 'var(--co-MyPacksUnopenedPackCard-titleColor, #FFFFFF)' }}
          >
            {offer.title}
          </h3>
          <p
            className="text-[13px] leading-[22px]"
            style={{ color: 'var(--co-MyPacksUnopenedPackCard-subtitleColor, #EAEAEB)' }}
          >
            {priceLabel}
          </p>
        </div>

        <Button
          variant="primary"
          size="md"
          onClick={() => onOpen(offer)}
          disabled={disabled}
        >
          Rip Now
        </Button>
      </div>
    </article>
  );
}